<template>
  <div>
    <!-- Ads list -->
    <div class="space-y-6">
      <div v-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3">
        {{ error }}
      </div>

      <div v-if="isLoading" class="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-6 text-gray-600">
        Loading ads...
      </div>

      <div v-else-if="!error && pagedAds.length === 0" class="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-6 text-gray-600">
        No ads found.
      </div>

      <template v-if="!isLoading && !error">
        <div
          v-for="ad in pagedAds"
          :key="ad.id"
          class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div class="p-5">
            <div class="flex flex-col lg:flex-row gap-5">
            <!-- Image -->
            <div class="w-full lg:w-56 shrink-0">
              <img
                :src="ad.image"
                :alt="ad.name"
                class="w-full h-40 lg:h-36 object-cover rounded-lg"
                loading="lazy"
              />
            </div>

            <!-- Main content -->
            <div class="flex-1 min-w-0">
              <div class="flex flex-col gap-4">
                <div class="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-3">
                      <h3 class="text-lg font-bold text-gray-900 truncate">
                        {{ ad.name }}
                      </h3>
                    </div>

                    <a
                      :href="ad.link"
                      class="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm mt-1"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Ad
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M14 3h7v7m0-7L10 14m-1 0H7a4 4 0 01-4-4V7a4 4 0 014-4h2"
                        />
                      </svg>
                    </a>
                  </div>

                  <!-- Stats -->
                  <div class="grid grid-cols-3 gap-3 w-full xl:w-auto">
                    <div class="bg-blue-50 rounded-lg px-4 py-3">
                      <div class="flex items-center gap-2 text-xs font-semibold text-blue-700 mb-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        Views
                      </div>
                      <div class="text-lg font-bold text-gray-900 tabular-nums">
                        {{ formatNumber(ad.views) }}
                      </div>
                    </div>

                    <div class="bg-green-50 rounded-lg px-4 py-3">
                      <div class="flex items-center gap-2 text-xs font-semibold text-green-700 mb-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                        Clicks
                      </div>
                      <div class="text-lg font-bold text-gray-900 tabular-nums">
                        {{ formatNumber(ad.clicks) }}
                      </div>
                    </div>

                    <div class="bg-purple-50 rounded-lg px-4 py-3">
                      <div class="text-xs font-semibold text-purple-700 mb-1">CTR</div>
                      <div class="text-lg font-bold text-gray-900 tabular-nums">
                        {{ formatPercent(ad.ctr) }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="border-t border-gray-100 pt-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div class="space-y-2">
                      <div class="flex items-center gap-2 text-sm text-gray-700">
                        <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <span class="text-gray-500">Contact:</span>
                        <span class="font-medium text-gray-900">{{ ad.contactName }}</span>
                      </div>

                      <div class="flex items-center gap-2 text-sm text-gray-700">
                        <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498A1 1 0 0121 16.72V20a2 2 0 01-2 2h-1C9.716 22 2 14.284 2 5V4a1 1 0 011-1z"
                          />
                        </svg>
                        <span class="text-gray-500">Phone:</span>
                        <span class="font-medium text-gray-900">{{ ad.phone }}</span>
                      </div>

                      <div class="flex items-center gap-2 text-sm text-gray-700">
                        <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-18 8h18a2 2 0 002-2V6a2 2 0 00-2-2H3a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        <span class="text-gray-500">Email:</span>
                        <a class="font-medium text-blue-600 hover:text-blue-700 truncate" :href="`mailto:${ad.email}`">
                          {{ ad.email }}
                        </a>
                      </div>
                    </div>

                    <div class="flex md:justify-end">
                      <div class="flex items-center gap-2 text-sm text-gray-700">
                        <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M8 7V3m8 4V3M5 11h14M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span class="text-gray-500 font-semibold">Campaign Period:</span>
                        <span class="font-medium text-gray-900">
                          {{ formatDate(ad.startDate) }}&nbsp;&nbsp;-&nbsp;&nbsp;{{ formatDate(ad.endDate) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-center gap-2 py-6">
      <button
        @click="goToPage(currentPage - 1)"
        :disabled="currentPage === 1"
        :class="[
          'px-3 py-1 rounded transition-colors',
          currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
        ]"
      >
        Previous
      </button>

      <button
        v-for="page in visiblePages"
        :key="page"
        @click="goToPage(page)"
        :class="[
          'px-3 py-1 rounded transition-colors',
          currentPage === page ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-100'
        ]"
      >
        {{ page }}
      </button>

      <span v-if="totalPages > 7" class="px-2 text-gray-500">...</span>

      <button
        v-if="totalPages > 7"
        @click="goToPage(totalPages)"
        :class="[
          'px-3 py-1 rounded transition-colors',
          currentPage === totalPages ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-100'
        ]"
      >
        {{ totalPages }}
      </button>

      <button
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage === totalPages"
        :class="[
          'px-3 py-1 rounded transition-colors',
          currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
        ]"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
import { AdsAPI } from '@/services/ads';

const svgDataUri = (title, subtitle) => {
  const safeTitle = String(title || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const safeSubtitle = String(subtitle || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="560" height="360" viewBox="0 0 560 360">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#EEF2FF"/>
          <stop offset="1" stop-color="#FDF2F8"/>
        </linearGradient>
      </defs>
      <rect width="560" height="360" rx="18" fill="url(#g)"/>
      <rect x="28" y="28" width="504" height="304" rx="14" fill="white" opacity="0.55"/>
      <text x="56" y="150" font-size="34" font-family="Arial, Helvetica, sans-serif" fill="#0F172A" font-weight="700">
        ${safeTitle}
      </text>
      <text x="56" y="196" font-size="18" font-family="Arial, Helvetica, sans-serif" fill="#334155">
        ${safeSubtitle}
      </text>
    </svg>
  `.trim();
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

export default {
  name: 'AdsTab',
  props: {
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 6,
      isLoading: false,
      error: null,
      apiTotalPages: 1,
      apiTotalCount: 0,
      ads: []
    };
  },
  computed: {
    filteredAds() {
      let filtered = this.ads;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(ad => {
          return (
            ad.name.toLowerCase().includes(query) ||
            ad.contactName.toLowerCase().includes(query) ||
            ad.email.toLowerCase().includes(query) ||
            ad.phone.toLowerCase().includes(query)
          );
        });
      }
      return filtered;
    },
    filteredAdsCount() {
      return this.filteredAds.length;
    },
    totalPages() {
      return Math.max(1, Number(this.apiTotalPages) || 1);
    },
    pagedAds() {
      // Server already paginates; we only filter within current page results.
      return this.filteredAds;
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    filteredAdsCount() {
      if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
      this.emitStats();
    }
  },
  mounted() {
    this.fetchAds(1);
  },
  methods: {
    async fetchAds(page) {
      this.isLoading = true;
      this.error = null;
      try {
        const resp = await AdsAPI.listAds({ page, per_page: this.pageSize });
        const payload = resp?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];

        this.ads = list.map((a) => ({
          id: a.ad_id ?? a.id,
          name: a.company_name ?? '',
          link: a.ad_link ?? '#',
          contactName: a.contact_person ?? '',
          phone: a.contact_number ?? '',
          email: a.contact_email ?? '',
          startDate: a.start_date ?? '',
          endDate: a.end_date ?? '',
          status: a.status ?? '',
          wording: a.ad_wording ?? '',
          views: Number(a.views || 0),
          clicks: Number(a.clicks || 0),
          ctr: Number(a.ctr || 0),
          image: a.ad_image || svgDataUri(a.company_name || 'Ad', a.ad_wording || '')
        }));

        this.currentPage = Number(payload.current_page) || page || 1;
        this.apiTotalPages = Number(payload.total_pages) || 1;
        this.apiTotalCount = Number(payload.total_count) || 0;
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || 'Failed to load ads';
        this.ads = [];
        this.apiTotalPages = 1;
        this.apiTotalCount = 0;
      } finally {
        this.isLoading = false;
        this.emitStats();
      }
    },
    emitStats() {
      const total = this.apiTotalCount || this.filteredAdsCount;
      this.$emit('update-stats', { totalAds: total });
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.fetchAds(page);
      }
    },
    formatNumber(n) {
      return Number(n || 0).toLocaleString();
    },
    formatPercent(v) {
      const n = Number(v || 0) * 100;
      return `${n.toFixed(2)} %`;
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      } catch {
        return iso;
      }
    }
  }
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>

